import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import ScrollTable from './ui/ScrollTable';
import Card from './ui/Card';
import { fetchAnalyticsJson } from '../utils/analyticsApi';
import { colors as hs } from '../theme/hindsightDark';
import useIsMobile from '../hooks/useIsMobile';
import { useFormat } from '../context/FormatContext';

const MIN_BALLS = 30;

const PHASES = ['powerplay', 'middle', 'death'];
const PHASE_LABELS = { powerplay: 'PP', middle: 'Mid', death: 'Death' };
const PHASE_NAMES = { powerplay: 'Powerplay', middle: 'Middle', death: 'Death' };

const formatShotName = (name) => {
  if (!name) return '';
  return name
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
};

// One hue, stronger = boundaries come more often (fewer balls per boundary).
const getBoundaryStyle = (ballsPerBoundary) => {
  if (ballsPerBoundary <= 4) return { bgcolor: '#3987e5', color: '#0a0c11' };
  if (ballsPerBoundary <= 6) return { bgcolor: 'rgba(57,135,229,0.55)', color: hs.textHi };
  if (ballsPerBoundary <= 10) return { bgcolor: 'rgba(57,135,229,0.25)', color: hs.textHi };
  return { bgcolor: 'rgba(255,255,255,0.06)', color: hs.textMed };
};

const getTopShots = (shots, totalBoundaries) => {
  if (!shots || totalBoundaries === 0) return [];
  return Object.entries(shots)
    .map(([name, data]) => ({ name, boundaries: data.boundaries || 0 }))
    .filter((s) => s.boundaries > 0)
    .sort((a, b) => b.boundaries - a.boundaries)
    .slice(0, 3)
    .map((s) => ({ ...s, pct: +(s.boundaries / totalBoundaries * 100).toFixed(0) }));
};

const getTotalBalls = (data) => {
  if (!data?.overall?.groups) return 0;
  return Object.values(data.overall.groups).reduce((sum, g) => sum + (g.total_balls || 0), 0);
};

const buildDetailRows = (groups) => {
  if (!groups) return [];
  return Object.entries(groups)
    .filter(([, data]) => data.total_balls > 0)
    .map(([key, data]) => ({ key, ...data }));
};

/* ---------- Stat Card ---------- */
const StatCard = ({ group, isMobile }) => {
  if (!group || group.total_balls === 0) {
    return (
      <Box sx={{
        p: isMobile ? 1 : 1.5, borderRadius: 1, border: `1px solid ${hs.border}`,
        bgcolor: hs.surface2, minHeight: 80,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Typography variant="caption" color="text.disabled">No data</Typography>
      </Box>
    );
  }

  if (group.boundaries === 0) {
    return (
      <Box sx={{
        p: isMobile ? 1 : 1.5, borderRadius: 1, border: `1px solid ${hs.border}`,
        bgcolor: hs.surface2, minHeight: 80,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      }}>
        <Typography variant="caption" color="text.disabled">No boundaries</Typography>
        <Typography variant="caption" color="text.disabled">({group.total_balls} balls)</Typography>
      </Box>
    );
  }

  const ballsPerBoundary = +(group.total_balls / group.boundaries).toFixed(1);
  const style = getBoundaryStyle(ballsPerBoundary);
  const topShots = getTopShots(group.shots, group.boundaries);

  return (
    <Box sx={{
      p: isMobile ? 1 : 1.5, borderRadius: 1, border: `1px solid ${hs.border}`,
      bgcolor: hs.surface2, display: 'flex', flexDirection: 'column', gap: 0.5,
    }}>
      {/* Balls per boundary — primary metric */}
      <Box sx={{
        display: 'inline-flex', alignSelf: 'flex-start',
        px: 1, py: 0.25, borderRadius: 1,
        bgcolor: style.bgcolor, color: style.color,
      }}>
        <Typography sx={{ fontWeight: 700, fontSize: isMobile ? '0.9rem' : '1.1rem' }}>
          {ballsPerBoundary} bpb
        </Typography>
      </Box>

      {/* 4s × 6s */}
      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
        {group.fours}×4&nbsp;&nbsp;{group.sixes}×6
      </Typography>

      {/* Balls */}
      <Typography variant="caption" sx={{ color: hs.textLo, fontSize: '0.75rem' }}>
        {group.total_balls} balls
      </Typography>

      {/* Top 3 shots */}
      {topShots.map((shot) => (
        <Typography key={shot.name} variant="caption" sx={{
          color: hs.textMed, fontSize: '0.75rem',
          lineHeight: 1.3,
        }}>
          {formatShotName(shot.name)}&nbsp;&nbsp;{shot.pct}%
        </Typography>
      ))}
    </Box>
  );
};


/* ---------- Build grid data ---------- */
const buildGridRows = (data, drillDown, context) => {
  if (!data) return [];

  const isBowler = context === 'bowler';

  if (drillDown && !isBowler) {
    // Bowl style drill-down: collect all styles across phases
    const allStyles = new Set();
    PHASES.forEach((phase) => {
      const groups = data.phases?.[phase]?.groups;
      if (!groups) return;
      Object.values(groups).forEach((g) => {
        if (g.styles) Object.keys(g.styles).forEach((s) => allStyles.add(s));
      });
    });

    return Array.from(allStyles).sort().map((style) => {
      const phaseData = {};
      PHASES.forEach((phase) => {
        const groups = data.phases?.[phase]?.groups;
        let merged = { total_balls: 0, boundaries: 0, fours: 0, sixes: 0, total_runs: 0, shots: {} };
        if (groups) {
          Object.values(groups).forEach((g) => {
            const s = g.styles?.[style];
            if (s) {
              merged.total_balls += s.total_balls || 0;
              merged.boundaries += s.boundaries || 0;
              merged.fours += s.fours || 0;
              merged.sixes += s.sixes || 0;
              merged.total_runs += s.total_runs || 0;
              if (s.shots) {
                Object.entries(s.shots).forEach(([shotName, shotData]) => {
                  if (!merged.shots[shotName]) merged.shots[shotName] = { boundaries: 0 };
                  merged.shots[shotName].boundaries += shotData.boundaries || 0;
                });
              }
            }
          });
        }
        merged.boundary_pct = merged.total_balls > 0 ? +(merged.boundaries / merged.total_balls * 100).toFixed(1) : 0;
        phaseData[phase] = merged;
      });
      return { label: style, phaseData };
    }).filter((row) => PHASES.some((p) => row.phaseData[p].total_balls > 0));
  }

  // Standard: pace/spin rows (or LHB/RHB for bowler)
  const groupKeys = data.overall?.groups ? Object.keys(data.overall.groups) : [];
  return groupKeys.map((gk) => {
    const phaseData = {};
    PHASES.forEach((phase) => {
      phaseData[phase] = data.phases?.[phase]?.groups?.[gk] || null;
    });
    return { label: gk.toUpperCase(), phaseData };
  });
};

/* ---------- Main component ---------- */
const BoundaryAnalysis = ({ context, name, startDate, endDate, leagues, includeInternational, topTeams, enabled = true }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [drillDown, setDrillDown] = useState(false);
  const [phase, setPhase] = useState('powerplay');

  const { isMobile } = useIsMobile();

  const { pinnedFormatParams } = useFormat();

  useEffect(() => {
    if (!enabled || !name || !context) return;

    setLoading(true);
    setError(null);

    const params = { context, name };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (leagues?.length) params.leagues = leagues;
    if (includeInternational) params.include_international = true;
    if (includeInternational && topTeams) params.top_teams = topTeams;
    params.format = pinnedFormatParams.format;
    params.gender = pinnedFormatParams.gender;

    fetchAnalyticsJson('/boundary-analysis', params)
      .then((result) => {
        setData(result);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [enabled, context, name, startDate, endDate, leagues, includeInternational, topTeams,
      pinnedFormatParams.format, pinnedFormatParams.gender]);

  if (loading) {
    return (
      <Card isMobile={isMobile}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      </Card>
    );
  }

  if (error) {
    return (
      <Card isMobile={isMobile}>
        <Alert severity="error" sx={{ mb: 1 }}>Failed to load boundary analysis</Alert>
      </Card>
    );
  }

  if (!data || getTotalBalls(data) < MIN_BALLS) return null;

  const gridRows = buildGridRows(data, drillDown, context);
  const isBowlerCtx = context === 'bowler';
  const dimLabel = isBowlerCtx ? 'Bat Hand' : 'Bowl Type';

  // Detail rows from overall for the summary table
  const detailRows = buildDetailRows(data.overall?.groups);

  return (
    <Card isMobile={isMobile}>
      <Typography variant="h6" gutterBottom>
        Boundary Analysis
      </Typography>

      {/* Controls */}
      {context !== 'bowler' && (
        <Box sx={{ mb: 2 }}>
          <ToggleButtonGroup
            value={drillDown ? 'style' : 'type'}
            exclusive
            onChange={(_, v) => v && setDrillDown(v === 'style')}
            size="small"
          >
            <ToggleButton value="type">Pace / Spin</ToggleButton>
            <ToggleButton value="style">Bowl Style</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Phones: one phase at a time, one full-width row per group. The 4-column grid left each
          card ~80px wide with 10px text at 360px. Desktop keeps the grid. */}
      {gridRows.length > 0 && isMobile && (
        <Box sx={{ mb: 3 }}>
          <ToggleButtonGroup
            value={phase}
            exclusive
            fullWidth
            onChange={(_, v) => v && setPhase(v)}
            size="small"
            sx={{ mb: 1.5 }}
          >
            {PHASES.map((p) => (
              <ToggleButton key={p} value={p} sx={{ minHeight: 36 }}>{PHASE_NAMES[p]}</ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Box sx={{ display: 'grid', gap: 1 }}>
            {gridRows.map((row) => (
              <Box key={row.label} sx={{ display: 'grid', gridTemplateColumns: '72px minmax(0, 1fr)', gap: 1, alignItems: 'stretch' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', fontWeight: 600, fontSize: '0.8125rem' }}>{row.label}</Box>
                <StatCard group={row.phaseData[phase]} isMobile={isMobile} />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {gridRows.length > 0 && !isMobile && (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr 1fr 1fr',
          gap: 1,
          mb: 3,
        }}>
          <Box />
          {PHASES.map((p) => (
            <Typography key={p} variant="caption" sx={{
              fontWeight: 600, textAlign: 'center', color: hs.textLo,
              textTransform: 'uppercase', letterSpacing: '0.05em', pb: 0.5,
            }}>
              {PHASE_LABELS[p]}
            </Typography>
          ))}
          {gridRows.map((row) => (
            <React.Fragment key={row.label}>
              <Box sx={{ display: 'flex', alignItems: 'center', fontWeight: 600, fontSize: '0.8rem', pr: 1, minWidth: 70 }}>
                {row.label}
              </Box>
              {PHASES.map((p) => (
                <StatCard key={p} group={row.phaseData[p]} isMobile={isMobile} />
              ))}
            </React.Fragment>
          ))}
        </Box>
      )}

      {/* Detail Table */}
      {detailRows.length > 0 && (
        <ScrollTable stickyFirstColumn>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{dimLabel}</TableCell>
                <TableCell align="right">Balls</TableCell>
                <TableCell align="right">Runs</TableCell>
                <TableCell align="right">4s</TableCell>
                <TableCell align="right">6s</TableCell>
                <TableCell align="right">Boundaries</TableCell>
                <TableCell align="right">Boundary %</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detailRows.map((row) => (
                <TableRow key={row.key}>
                  <TableCell sx={{ fontWeight: 500 }}>{row.key.toUpperCase()}</TableCell>
                  <TableCell align="right">{row.total_balls}</TableCell>
                  <TableCell align="right">{row.total_runs}</TableCell>
                  <TableCell align="right">{row.fours}</TableCell>
                  <TableCell align="right">{row.sixes}</TableCell>
                  <TableCell align="right">{row.boundaries}</TableCell>
                  <TableCell align="right">{row.boundary_pct}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollTable>
      )}
    </Card>
  );
};

export default BoundaryAnalysis;
